import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Loader2, X } from 'lucide-react';

/**
 * Pro-only confirmation dialog for deleting one or more matches from a
 * generated schedule. Requires the user to type "DELETE" (all caps)
 * before the destructive action is enabled — matches the pattern the
 * backend enforces via `?confirm=DELETE`.
 *
 * Props:
 *  - `match`  → single-match delete mode (shows matchup summary)
 *  - `matches` → bulk-delete mode (shows count summary)
 *  Exactly one of the two should be provided.
 */
export function DeleteMatchDialog({ open, match, matches, onCancel, onConfirm }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const isBulk = Array.isArray(matches) && matches.length > 0;
  const bulkCount = isBulk ? matches.length : 0;

  // Reset the typed value every time the dialog re-opens.
  useEffect(() => {
    if (open) setText('');
  }, [open, match?.id, bulkCount]);

  if (!open || (!match && !isBulk)) return null;

  const canDelete = text.trim() === 'DELETE' && !busy;

  const handleConfirm = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      if (isBulk) {
        await onConfirm(matches);
      } else {
        await onConfirm(match);
      }
    } finally {
      setBusy(false);
    }
  };

  // ------- Summary block ---------------------------------------------------
  let summary;
  if (isBulk) {
    // Group by date so cancelling a whole day reads naturally.
    const byDate = matches.reduce((acc, m) => {
      const d = m.date || 'Unscheduled';
      acc[d] = (acc[d] || 0) + 1;
      return acc;
    }, {});
    const dateChips = Object.entries(byDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(0, 4);
    summary = (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
          Bulk delete
        </div>
        <div className="text-sm font-semibold text-slate-800 mt-0.5" data-testid="delete-match-target">
          {bulkCount} match{bulkCount === 1 ? '' : 'es'} selected
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {dateChips.map(([d, count]) => (
            <span key={d}
              className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-700">
              {d} · {count}
            </span>
          ))}
          {Object.keys(byDate).length > dateChips.length && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-500">
              +{Object.keys(byDate).length - dateChips.length} more
            </span>
          )}
        </div>
      </div>
    );
  } else {
    const stageLabel = match.stage === 'manual'
      ? 'Manual match'
      : (match.stage || 'Match');
    const matchupLabel =
      `${match.team_a_name || match.team_a || 'Team A'} vs ${match.team_b_name || match.team_b || 'Team B'}`;
    summary = (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
          {stageLabel}
          {match.date ? ` · ${match.date}` : ''}
          {match.time ? ` · ${match.time}` : ''}
        </div>
        <div className="text-sm font-semibold text-slate-800 mt-0.5" data-testid="delete-match-target">
          {matchupLabel}
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      data-testid="delete-match-dialog"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <Card className="w-full max-w-md border-rose-200 shadow-2xl">
        <CardHeader className="border-b bg-rose-50/60">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-rose-600 text-white flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <CardTitle className="text-rose-900 text-lg">
                  {isBulk
                    ? `Delete ${bulkCount} match${bulkCount === 1 ? '' : 'es'}?`
                    : 'Delete this match?'}
                </CardTitle>
                <p className="text-xs text-rose-800/80 mt-1">
                  This permanently removes {isBulk ? 'them' : 'it'} from the schedule. This cannot be undone.
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-slate-500 hover:text-slate-800"
              onClick={onCancel}
              disabled={busy}
              data-testid="delete-match-cancel-x"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-5 pb-5 space-y-4">
          {summary}

          <div className="space-y-1.5">
            <Label htmlFor="delete-match-input" className="text-slate-700 text-sm">
              Type <span className="font-mono font-bold text-rose-700">DELETE</span> to confirm
            </Label>
            <Input
              id="delete-match-input"
              data-testid="delete-match-confirm-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="DELETE"
              autoFocus
              autoComplete="off"
              spellCheck={false}
              className="font-mono tracking-wider"
              disabled={busy}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canDelete) handleConfirm();
                if (e.key === 'Escape' && !busy) onCancel();
              }}
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={busy}
              data-testid="delete-match-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!canDelete}
              className="bg-rose-600 hover:bg-rose-700 text-white disabled:bg-rose-300"
              data-testid="delete-match-confirm-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {isBulk ? `Delete ${bulkCount} match${bulkCount === 1 ? '' : 'es'}` : 'Delete match'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
